// src/games/quiz/quizGroqService.js
// Uses Groq API for fast LLM-based question generation; falls back to built-in question bank on any error.
// Free API key: https://console.groq.com/keys (free tier available)

import { getFallbackQuestions } from './quizFallbackBank';

const GROQ_API_URL = 'https://api.groq.com/openai/v1/chat/completions';
const GROQ_MODEL = 'llama-3.3-70b-versatile';

export async function generateQuizQuestions(topic, count = 8, apiKey) {
  if (!apiKey) {
    console.info('ℹ️ No REACT_APP_GROQ_API_KEY — using built-in question bank.');
    return getFallbackQuestions(topic, count);
  }

  console.info(`🧠 Calling Groq for topic: "${topic}" (${count} questions)…`);
  try {
    const questions = await fetchFromGroq(topic, count, apiKey);
    console.info(`✅ Groq returned ${questions.length} questions.`);
    if (questions.length >= count) return questions;
    // Top up from fallback if Groq returned fewer than requested
    const extra = getFallbackQuestions(topic, count - questions.length);
    return [...questions, ...extra].slice(0, count);
  } catch (err) {
    if (err.message?.includes('429') || err.message?.includes('rate limit')) {
      console.warn('⚠️ Groq quota hit (429). Using built-in bank.\nFix: get a key from console.groq.com/keys — free tier available.');
    } else if (err.message?.includes('401') || err.message?.includes('Unauthorized') || err.message?.includes('API_KEY_INVALID')) {
      console.warn('⚠️ Groq API key is invalid. Check REACT_APP_GROQ_API_KEY in your .env file.');
    } else {
      console.warn('⚠️ Groq error — using built-in bank:', err.message);
    }
    return getFallbackQuestions(topic, count);
  }
}

async function fetchFromGroq(topic, count, apiKey) {
  const prompt = `Generate ${count} multiple-choice trivia questions about "${topic}".

Rules:
- Each question must have exactly 4 answer options.
- Exactly one option must be correct.
- Questions should be factual, clear, and varied in difficulty.
- Do NOT repeat similar questions.
- For every question, provide a legitimate, high-quality resource link (URL) that verifies the answer.
- Return ONLY a JSON array, no markdown, no explanation.

Format (strict JSON array):
[
  {
    "question": "Question text here?",
    "options": ["Option A", "Option B", "Option C", "Option D"],
    "correctIndex": 0,
    "resource": "https://example.com/verification-link"
  }
]

correctIndex is the 0-based index of the correct option.`;

  const res = await fetch(GROQ_API_URL, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${apiKey}`,
    },
    body: JSON.stringify({
      messages: [{ role: 'user', content: prompt }],
      model: GROQ_MODEL,
      temperature: 0.7,
      max_tokens: 2048,
    }),
  });

  if (!res.ok) {
    const body = await res.text();
    throw new Error(`Groq API error ${res.status}: ${body}`);
  }

  const data = await res.json();
  const raw = data.choices?.[0]?.message?.content || '';
  const cleaned = raw.replace(/```json\s*/gi, '').replace(/```/g, '').trim();
  console.debug('Groq raw response:', raw);
  let parsed;
  try { parsed = JSON.parse(cleaned); }
  catch { throw new Error('Failed to parse Groq response as JSON'); }

  return parsed
    .filter(q => q.question && Array.isArray(q.options) && q.options.length === 4
                 && Number.isInteger(q.correctIndex) && q.correctIndex >= 0 && q.correctIndex < 4)
    .slice(0, count)
    .map(q => ({
      question:     q.question,
      options:      q.options.map(String),
      correctIndex: q.correctIndex,
      resource:     q.resource || '',
    }));
}
